import {
  useCompanyVisibilityState,
  useSearchTableFromRoute,
} from '@aibees/elephant/hooks';
import { SearchTable } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { Badge, Box, Tooltip } from '@mui/material';
import { memo, type PropsWithChildren } from 'react';
import { getTooltipTitle } from './config';
import type { CompanyInfoBadgeProps as Props } from './types';

export const CompanyInfoBadge = memo(
  ({
    className: _className,
    children,
    badgeSize,
    tooltipPlacement,
    personData,
  }: PropsWithChildren<Props>) => {
    const table = useSearchTableFromRoute();
    const isCompanySearch = table === SearchTable.Company;

    const {
      hasFullCompanyData,
      shouldShowCompanyInfoBadge,
      hasOnlyCompanyName,
      isSelfEmployedOrFreelancer,
      employmentType,
      isNoLongerEmployed,
      wasPreviouslySelfEmployedOrFreelancer,
      previousEmploymentType,
      noPreviousCompanyDataAvailable,
      hasOnlyPreviousCompanyName,
      noCompanyDataAvailable,
      showNoDataAvailable,
    } = useCompanyVisibilityState(personData);

    if (isCompanySearch && !personData) {
      return <>{children}</>;
    }

    const shouldShowBadge =
      hasOnlyCompanyName ||
      noCompanyDataAvailable ||
      isSelfEmployedOrFreelancer ||
      shouldShowCompanyInfoBadge ||
      isNoLongerEmployed ||
      showNoDataAvailable;

    if (!shouldShowBadge && hasFullCompanyData) {
      return <>{children}</>;
    }

    const tooltipTitle = getTooltipTitle({
      isSelfEmployedOrFreelancer,
      employmentType,
      hasOnlyCompanyName,
      isNoLongerEmployed,
      noPreviousCompanyDataAvailable,
      hasOnlyPreviousCompanyName,
      wasPreviouslySelfEmployedOrFreelancer,
      previousEmploymentType,
    });

    const shouldShowWarning = isNoLongerEmployed || shouldShowCompanyInfoBadge;

    return (
      <Badge
        color={shouldShowWarning ? 'warning' : 'default'}
        variant="soft"
        size={badgeSize ?? 16}
        badgeContent={
          <Tooltip
            placement={tooltipPlacement ?? 'bottom'}
            title={tooltipTitle}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Icon
                name={shouldShowWarning ? 'undo' : 'info'}
                color={shouldShowWarning ? 'warning' : 'default'}
              />
            </Box>
          </Tooltip>
        }
      >
        {children}
      </Badge>
    );
  }
);

CompanyInfoBadge.displayName = 'CompanyInfoBadge';
